import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import {
  canonicalTetrisDeckKeys,
  canonicalTetrisMemberKey,
  mergeTetrisCodeSets,
  tetrisDeckDisplayCodes,
} from './raidTetrisIdentity.js';

const UNAVAILABLE_MINUTES = Number.MAX_SAFE_INTEGER;
const TETRIS_ACCURACY_TOLERANCE_EOK = 5;

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function resultAccuracy(target, total) {
  const absoluteErrorEok = Math.abs(numberValue(total) - numberValue(target));
  return {
    absoluteErrorEok,
    withinTolerance: absoluteErrorEok < TETRIS_ACCURACY_TOLERANCE_EOK,
  };
}

function memberKey(value = {}) {
  return canonicalTetrisMemberKey(value);
}

function timeMinutes(value) {
  const match = textValue(value).match(/^(\d{2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 ? hour * 60 + minute : null;
}

function normalizeAvailabilityWindows(member = {}) {
  const source = member.availabilityWindows ?? member.availability_windows ?? [];
  if (Array.isArray(source) && source.length) {
    return source.map((item) => ({
      start: textValue(item?.start ?? item?.start_at).slice(0, 5),
      end: textValue(item?.end ?? item?.end_at).slice(0, 5),
    })).filter((item) => timeMinutes(item.start) !== null && timeMinutes(item.end) !== null && item.start !== item.end);
  }
  const legacy = textValue(member.availableAt ?? member.available_at);
  if (!legacy) return [];
  const start = timeMinutes(legacy);
  if (start === null) return [];
  const end = (start + 60) % 1440;
  return [{ start: legacy.slice(0, 5), end: `${String(Math.floor(end / 60)).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}` }];
}

function availabilityAtNow(windows, now = new Date()) {
  const current = now.getHours() * 60 + now.getMinutes();
  if (!windows.length) return { isAvailableNow: false, minutesUntilAvailable: UNAVAILABLE_MINUTES, availabilityLabel: '미설정' };
  let nextWait = UNAVAILABLE_MINUTES;
  for (const window of windows) {
    const start = timeMinutes(window.start);
    const end = timeMinutes(window.end);
    if (start === null || end === null) continue;
    const active = start < end ? current >= start && current < end : current >= start || current < end;
    if (active) return { isAvailableNow: true, minutesUntilAvailable: 0, availabilityLabel: '현재 접속 가능' };
    const wait = (start - current + 1440) % 1440;
    if (wait < nextWait) nextWait = wait;
  }
  return {
    isAvailableNow: false,
    minutesUntilAvailable: nextWait,
    availabilityLabel: nextWait === UNAVAILABLE_MINUTES ? '미설정' : `${nextWait}분 후 접속`,
  };
}

function waitPenaltyEok(minutes) {
  if (minutes === 0) return 0;
  if (!Number.isFinite(minutes) || minutes === UNAVAILABLE_MINUTES) return 250;
  if (minutes <= 30) return 25;
  if (minutes <= 60) return 50;
  if (minutes <= 120) return 100;
  if (minutes <= 240) return 160;
  return 220;
}

function stateAvailabilityPenalty(state) {
  const byMember = new Map();
  for (const item of state.items) {
    const current = byMember.get(item.memberOpenid);
    const wait = numberValue(item.minutesUntilAvailable, UNAVAILABLE_MINUTES);
    if (current === undefined || wait < current) byMember.set(item.memberOpenid, wait);
  }
  return [...byMember.values()].reduce((sum, wait) => sum + waitPenaltyEok(wait), 0);
}

function recommendationScore(state, target, preferAvailability = false) {
  return Math.abs(state.total - target) + (preferAvailability ? stateAvailabilityPenalty(state) : 0);
}

function collectActualUsage(actualRecords = [], catalog = null) {
  const countByMember = new Map();
  const usedCodesByMember = new Map();
  for (const record of Array.isArray(actualRecords) ? actualRecords : []) {
    const openid = memberKey(record);
    if (!openid) continue;
    countByMember.set(openid, Math.min(3, (countByMember.get(openid) || 0) + 1));
    const used = usedCodesByMember.get(openid) || new Set();
    canonicalTetrisDeckKeys(record.deck ?? record.deck_codes ?? record.deckCodes, catalog).forEach((code) => used.add(code));
    usedCodesByMember.set(openid, used);
  }
  return { countByMember, usedCodesByMember };
}

function burstStep(value) {
  const raw = textValue(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  if (raw === 'step1' || raw === 'burst1' || raw === 'b1' || raw === '1') return 1;
  if (raw === 'step2' || raw === 'burst2' || raw === 'b2' || raw === '2') return 2;
  if (raw === 'step3' || raw === 'burst3' || raw === 'b3' || raw === '3') return 3;
  return 0;
}

function isExecutableMeasuredDeck(deck = [], catalog = null) {
  if (!catalog?.byKey || !Array.isArray(deck) || deck.length !== 5) return true;
  const rows = deck.map((code) => findNikkeInCatalog(catalog, code));
  if (rows.some((row) => !row)) return true;
  const hasBurst1 = rows.some((row) => burstStep(row.burst ?? row.burstCode) === 1)
    || rows.some((row) => textValue(row.name) === '라피 : 레드 후드');
  const hasBurst2 = rows.some((row) => burstStep(row.burst ?? row.burstCode) === 2);
  const hasBurst3 = rows.some((row) => burstStep(row.burst ?? row.burstCode) === 3);
  return hasBurst1 && hasBurst2 && hasBurst3;
}

function eligibleCurrentMemberIds(memberRows = []) {
  const roster = new Set((Array.isArray(memberRows) ? memberRows : [])
    .filter((member) => member?.source === 'roster' && member?.active !== false)
    .map(memberKey)
    .filter(Boolean));
  if (roster.size) return roster;
  return new Set((Array.isArray(memberRows) ? memberRows : [])
    .filter((member) => member?.active !== false)
    .map(memberKey)
    .filter(Boolean));
}

export function normalizeTetrisPracticeRecord(raw = {}, nicknameByOpenid = new Map(), catalog = null) {
  const openid = memberKey(raw);
  const rawDeck = raw.deck ?? raw.deck_codes ?? raw.deckCodes;
  const deck = tetrisDeckDisplayCodes(rawDeck, catalog);
  const canonicalDeck = canonicalTetrisDeckKeys(rawDeck, catalog);
  return {
    id: textValue(raw.id) || `${openid}:${numberValue(raw.boss_no ?? raw.bossNo)}:${numberValue(raw.slot_no ?? raw.slotNo, 1)}`,
    memberOpenid: openid,
    nickname: textValue(raw.memberNickname ?? raw.member_nickname ?? raw.nickname) || nicknameByOpenid.get(openid) || '닉네임 확인 불가',
    bossNo: numberValue(raw.boss_no ?? raw.bossNo),
    slotNo: numberValue(raw.slot_no ?? raw.slotNo, 1),
    title: textValue(raw.title) || `모의전 ${numberValue(raw.slot_no ?? raw.slotNo, 1)}`,
    damageEok: numberValue(raw.damage_eok ?? raw.damageEok ?? raw.damage),
    deck,
    canonicalDeck,
    used: Boolean(raw.used),
  };
}

export function buildTetrisCandidates({
  members = [],
  actualRecords = [],
  practiceRecords = [],
  selectedMemberIds = null,
  selectedPracticeIds = null,
  preferAvailability = false,
  currentAvailabilityOnly = false,
  ignoreStoredAvailability = false,
  catalog = null,
} = {}) {
  const memberRows = Array.isArray(members) ? members : [];
  const currentMemberIds = eligibleCurrentMemberIds(memberRows);
  const nicknameByOpenid = new Map(memberRows.map((member) => [memberKey(member), textValue(member.nickname)]));
  const availabilityByOpenid = new Map(memberRows.map((member) => {
    const windows = normalizeAvailabilityWindows(member);
    return [memberKey(member), { availabilityWindows: windows, ...availabilityAtNow(windows) }];
  }));
  const { countByMember: actualCountByMember, usedCodesByMember: actualUsedCodesByMember } = collectActualUsage(actualRecords, catalog);
  const selectedMembers = selectedMemberIds instanceof Set ? selectedMemberIds : null;
  const selectedSlots = selectedPracticeIds instanceof Set ? selectedPracticeIds : null;
  const deduped = new Map();

  for (const raw of Array.isArray(practiceRecords) ? practiceRecords : []) {
    const record = normalizeTetrisPracticeRecord(raw, nicknameByOpenid, catalog);
    if (!record.memberOpenid || record.bossNo < 1 || record.bossNo > 5) continue;
    if (currentMemberIds.size && !currentMemberIds.has(record.memberOpenid)) continue;
    if (record.damageEok <= 0 || record.used) continue;
    if (record.deck.length !== 5 || record.canonicalDeck.length !== 5 || new Set(record.canonicalDeck).size !== 5) continue;
    if (!isExecutableMeasuredDeck(record.deck, catalog)) continue;
    if (selectedMembers && !selectedMembers.has(record.memberOpenid)) continue;
    if (selectedSlots && !selectedSlots.has(record.id)) continue;

    const actualUsedCodes = actualUsedCodesByMember.get(record.memberOpenid) || new Set();
    if (record.canonicalDeck.some((code) => actualUsedCodes.has(code))) continue;

    const candidate = {
      ...record,
      ...(availabilityByOpenid.get(record.memberOpenid) || { availabilityWindows: [], isAvailableNow: false, minutesUntilAvailable: UNAVAILABLE_MINUTES, availabilityLabel: '미설정' }),
      actualAttackCount: actualCountByMember.get(record.memberOpenid) || 0,
      actualUsedCodes: [...actualUsedCodes],
      remainingAttacks: Math.max(0, 3 - (actualCountByMember.get(record.memberOpenid) || 0)),
    };
    if (!candidate.remainingAttacks) continue;
    if (!ignoreStoredAvailability && currentAvailabilityOnly && !candidate.isAvailableNow) continue;

    const current = deduped.get(candidate.id);
    if (!current || candidate.damageEok > current.damageEok) deduped.set(candidate.id, candidate);
  }

  return [...deduped.values()]
    .sort((a, b) => a.bossNo - b.bossNo
      || (!ignoreStoredAvailability && preferAvailability ? a.minutesUntilAvailable - b.minutesUntilAvailable : 0)
      || b.damageEok - a.damageEok
      || a.nickname.localeCompare(b.nickname, 'ko-KR'));
}

function sameMemberOptions(candidates, remainingAttacks, blockedCodes, preferAvailability = false) {
  const seen = new Set();
  const source = [...candidates]
    .filter((candidate) => {
      if (!candidate?.id || seen.has(candidate.id)) return false;
      seen.add(candidate.id);
      return true;
    })
    .sort((a, b) => (preferAvailability ? numberValue(a.minutesUntilAvailable, UNAVAILABLE_MINUTES) - numberValue(b.minutesUntilAvailable, UNAVAILABLE_MINUTES) : 0)
      || b.damageEok - a.damageEok
      || a.slotNo - b.slotNo);
  const options = [{ items: [], total: 0, codes: new Set() }];
  const picked = [];

  function visit(startIndex, total, codes) {
    if (picked.length >= remainingAttacks) return;
    for (let cursor = startIndex; cursor < source.length; cursor += 1) {
      const candidate = source[cursor];
      if (candidate.canonicalDeck.some((code) => blockedCodes.has(code) || codes.has(code))) continue;
      const nextCodes = new Set(codes);
      candidate.canonicalDeck.forEach((code) => nextCodes.add(code));
      picked.push(candidate);
      options.push({ items: [...picked], total: total + candidate.damageEok, codes: nextCodes });
      visit(cursor + 1, total + candidate.damageEok, nextCodes);
      picked.pop();
    }
  }

  visit(0, 0, new Set());
  return options;
}

function stateKey(state) {
  return state.items.map((item) => item.id).sort().join('|');
}

function isValidTetrisState(state, memberUsage = new Map(), memberUsedCodes = new Map()) {
  const usage = new Map(memberUsage);
  const codesByMember = new Map();
  for (const [openid, values] of memberUsedCodes.entries()) codesByMember.set(openid, mergeTetrisCodeSets(values));
  const ids = new Set();

  for (const item of state.items) {
    if (!item?.id || ids.has(item.id)) return false;
    ids.add(item.id);
    const openid = item.memberOpenid;
    if (!openid) return false;

    const nextUsage = (usage.get(openid) || 0) + 1;
    if (nextUsage > numberValue(item.remainingAttacks)) return false;
    usage.set(openid, nextUsage);

    if (!Array.isArray(item.canonicalDeck) || item.canonicalDeck.length !== 5 || new Set(item.canonicalDeck).size !== 5) return false;
    const used = mergeTetrisCodeSets(codesByMember.get(openid), item.actualUsedCodes);
    if (item.canonicalDeck.some((code) => used.has(code))) return false;
    item.canonicalDeck.forEach((code) => used.add(code));
    codesByMember.set(openid, used);
  }
  return true;
}

function pruneLegacyStates(states, target, beamWidth, preferAvailability = false) {
  const deduped = new Map();
  for (const state of states) {
    const key = stateKey(state);
    if (!deduped.has(key)) deduped.set(key, state);
  }
  const unique = [...deduped.values()];
  const over = unique.filter((state) => state.total >= target)
    .sort((a, b) => recommendationScore(a, target, preferAvailability) - recommendationScore(b, target, preferAvailability) || a.items.length - b.items.length || a.total - b.total);
  const under = unique.filter((state) => state.total < target)
    .sort((a, b) => recommendationScore(a, target, preferAvailability) - recommendationScore(b, target, preferAvailability) || b.total - a.total || a.items.length - b.items.length);
  const half = Math.max(50, Math.floor(beamWidth / 2));
  return [...over.slice(0, half), ...under.slice(0, half)];
}

function nearestStateSort(target, preferAvailability = false) {
  return (a, b) => recommendationScore(a, target, preferAvailability) - recommendationScore(b, target, preferAvailability)
    || a.items.length - b.items.length
    || ((a.total >= target) !== (b.total >= target) ? (a.total >= target ? -1 : 1) : 0)
    || a.total - b.total;
}

export function findBossTetrisCombinations({ candidates = [], bossNo, targetHpEok, mode = 'kill', topN = 5, maxSlots = 24, memberUsage = new Map(), memberUsedCodes = new Map(), beamWidth = 1400, preferAvailability = false } = {}) {
  const target = Math.max(0, numberValue(targetHpEok));
  if (!target) return { target, results: [], insufficient: null };
  const resolvedMode = mode === 'kill' || mode === 'under' ? mode : 'nearest';

  const byMember = new Map();
  for (const candidate of (Array.isArray(candidates) ? candidates : []).filter((item) => item.bossNo === Number(bossNo))) {
    const list = byMember.get(candidate.memberOpenid) || [];
    list.push(candidate);
    byMember.set(candidate.memberOpenid, list);
  }

  let states = [{ items: [], total: 0 }];
  const memberEntries = [...byMember.entries()].sort((a, b) => {
    const waitA = numberValue(a[1][0]?.minutesUntilAvailable, UNAVAILABLE_MINUTES);
    const waitB = numberValue(b[1][0]?.minutesUntilAvailable, UNAVAILABLE_MINUTES);
    const maxA = Math.max(0, ...a[1].map((item) => item.damageEok));
    const maxB = Math.max(0, ...b[1].map((item) => item.damageEok));
    return (preferAvailability ? waitA - waitB : 0) || maxB - maxA;
  });

  for (const [openid, memberCandidates] of memberEntries) {
    const remaining = Math.max(0, Number(memberCandidates[0]?.remainingAttacks || 0) - (memberUsage.get(openid) || 0));
    if (!remaining) continue;
    const blockedCodes = mergeTetrisCodeSets(
      memberCandidates.flatMap((candidate) => candidate.actualUsedCodes || []),
      memberUsedCodes.get(openid),
    );
    const options = sameMemberOptions(memberCandidates, remaining, blockedCodes, preferAvailability);
    const next = [];
    for (const state of states) for (const option of options) {
      if (state.items.length + option.items.length <= maxSlots) next.push({ items: [...state.items, ...option.items], total: state.total + option.total });
    }
    states = pruneLegacyStates(next, target, beamWidth, preferAvailability);
  }

  const validStates = states.filter((state) => state.items.length > 0 && isValidTetrisState(state, memberUsage, memberUsedCodes));
  const over = validStates.filter((state) => state.total >= target)
    .sort((a, b) => recommendationScore(a, target, preferAvailability) - recommendationScore(b, target, preferAvailability) || a.items.length - b.items.length || a.total - b.total);
  const under = validStates.filter((state) => state.total <= target)
    .sort((a, b) => recommendationScore(a, target, preferAvailability) - recommendationScore(b, target, preferAvailability) || a.items.length - b.items.length || b.total - a.total);
  const nearest = [...validStates].sort(nearestStateSort(target, preferAvailability));
  const chosen = resolvedMode === 'nearest' ? nearest : resolvedMode === 'under' ? under : over;
  const results = chosen.slice(0, Math.max(1, topN)).map((state, index) => {
    const accuracy = resultAccuracy(target, state.total);
    return {
      rank: index + 1,
      items: state.items,
      totalEok: state.total,
      diffEok: state.total - target,
      absoluteErrorEok: accuracy.absoluteErrorEok,
      withinTolerance: accuracy.withinTolerance,
      availabilityPenaltyEok: preferAvailability ? stateAvailabilityPenalty(state) : 0,
      sufficient: state.total >= target,
    };
  });
  const bestUnder = under[0] || null;
  return { target, results, insufficient: resolvedMode === 'kill' && !results.length && bestUnder ? { items: bestUnder.items, totalEok: bestUnder.total, diffEok: bestUnder.total - target, shortageEok: target - bestUnder.total } : null };
}

function cloneUsedCodes(source = new Map()) {
  const next = new Map();
  for (const [openid, codes] of source.entries()) next.set(openid, mergeTetrisCodeSets(codes));
  return next;
}

function applySelectedResult(result, memberUsage, memberUsedCodes) {
  const nextUsage = new Map(memberUsage);
  const nextCodes = cloneUsedCodes(memberUsedCodes);
  for (const item of result?.items || []) {
    nextUsage.set(item.memberOpenid, (nextUsage.get(item.memberOpenid) || 0) + 1);
    const used = mergeTetrisCodeSets(nextCodes.get(item.memberOpenid), item.actualUsedCodes);
    item.canonicalDeck.forEach((code) => used.add(code));
    nextCodes.set(item.memberOpenid, used);
  }
  return { memberUsage: nextUsage, memberUsedCodes: nextCodes };
}

export function buildGlobalTetrisPlan({ bosses = [], candidates = [], targets = {}, mode = 'kill', preferAvailability = false } = {}) {
  const candidateTotalByBoss = new Map();
  for (const candidate of Array.isArray(candidates) ? candidates : []) {
    candidateTotalByBoss.set(candidate.bossNo, (candidateTotalByBoss.get(candidate.bossNo) || 0) + candidate.damageEok);
  }
  const order = (Array.isArray(bosses) ? bosses : [])
    .filter((boss) => numberValue(targets[boss.bossNo]) > 0 && !boss.isCleared)
    .sort((a, b) => {
      const scarcityA = numberValue(targets[a.bossNo]) / Math.max(candidateTotalByBoss.get(a.bossNo) || 0, 0.000001);
      const scarcityB = numberValue(targets[b.bossNo]) / Math.max(candidateTotalByBoss.get(b.bossNo) || 0, 0.000001);
      return scarcityB - scarcityA || numberValue(targets[b.bossNo]) - numberValue(targets[a.bossNo]);
    });

  const resolvedMode = mode === 'kill' || mode === 'under' ? mode : 'nearest';
  const memberUsage = new Map();
  const memberUsedCodes = new Map();
  const byBoss = new Map();
  for (const boss of order) {
    const recommendation = findBossTetrisCombinations({
      candidates,
      bossNo: boss.bossNo,
      targetHpEok: targets[boss.bossNo],
      mode: resolvedMode,
      topN: 3,
      memberUsage,
      memberUsedCodes,
      preferAvailability,
    });
    const selected = recommendation.results.find((result) => isValidTetrisState({ items: result.items, total: result.totalEok }, memberUsage, memberUsedCodes)) || null;
    byBoss.set(boss.bossNo, { ...recommendation, selected });
    if (!selected) continue;
    const resources = applySelectedResult(selected, memberUsage, memberUsedCodes);
    memberUsage.clear();
    resources.memberUsage.forEach((value, key) => memberUsage.set(key, value));
    memberUsedCodes.clear();
    resources.memberUsedCodes.forEach((value, key) => memberUsedCodes.set(key, value));
  }
  return { order, byBoss, memberUsage, memberUsedCodes, optimizationProfileVersion: '' };
}
